import {
  AllCurrencyCodesSchema,
  BaseResponseSchema,
  getRequiredMessage,
} from '@monetize/types/common';
import { string, z } from 'zod';
import { PaymentMethodTypeEnumZ } from './paymentMethod.types';

export enum PaymentGatewayTypeEnum {
  STRIPEV2 = 'STRIPEV2',
  BRAINTREE = 'BRAINTREE',
  WORLDPAY = 'WORLDPAY',
  AUTHNET = 'AUTHNET',
  ADYEN = 'ADYEN',
  VANTIV = 'VANTIV',
  PAYPAL_PRO = 'PAYPAL_PRO',
  PAYMENT_TECH = 'PAYMENT_TECH',
  TEST_PASS = 'TEST_PASS',
  TEST_FAIL = 'TEST_FAIL',
}

export enum PaymentGatewayStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const PaymentGatewayStatusDisplayText: {
  [key in PaymentGatewayStatusEnum]: string;
} = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

export const PaymentGatewayTypeEnumZ = z.enum(PaymentGatewayTypeEnum);
export const PaymentGatewayStatusEnumZ = z.enum(PaymentGatewayStatusEnum);

export const PaymentMethodEnabledSchema = z.object({
  method: PaymentMethodTypeEnumZ,
  enabled: z.boolean(),
});
export type IPaymentMethodEnabledSchema = z.infer<
  typeof PaymentMethodEnabledSchema
>;

export const PaymentGatewayAmountConfigurationSchema = z.object({
  currency: AllCurrencyCodesSchema,
  minimum: z
    .union([z.string(), z.number()])
    .transform((val) => Number(val))
    .nullish(),
  maximum: z
    .union([z.string(), z.number()])
    .transform((val) => (val === '' ? null : Number(val)))
    .nullish(),
});

// Form schema without transform for react-hook-form compatibility
export const PaymentGatewayAmountConfigurationFormSchema = z.object({
  currency: AllCurrencyCodesSchema,
  minimum: z.union([z.string(), z.number()]).nullish(),
  maximum: z.union([z.string(), z.number()]).nullish(),
});
export type PaymentGatewayAmountConfigurationForm = z.infer<
  typeof PaymentGatewayAmountConfigurationFormSchema
>;

export const PaymentGatewayConfigurationSchema = z.object({
  type: PaymentMethodTypeEnumZ,
  amountConfigurations: PaymentGatewayAmountConfigurationSchema.array(),
});

// Form schema without transform for react-hook-form compatibility
export const PaymentGatewayConfigurationFormSchema = z.object({
  type: PaymentMethodTypeEnumZ,
  amountConfigurations: PaymentGatewayAmountConfigurationFormSchema.array(),
});
export type PaymentGatewayConfigurationForm = z.infer<
  typeof PaymentGatewayConfigurationFormSchema
>;

export const PaymentGatewayConfigurationsSchema = z.object({
  configurations: PaymentGatewayConfigurationSchema.array(),
});

// Form schema without transform for react-hook-form compatibility
export const PaymentGatewayConfigurationsFormSchema = z.object({
  configurations: PaymentGatewayConfigurationFormSchema.array(),
});
export type PaymentGatewayConfigurationsForm = z.infer<
  typeof PaymentGatewayConfigurationsFormSchema
>;

export type PaymentGatewayConfigurations = z.infer<
  typeof PaymentGatewayConfigurationsSchema
>;

export const PaymentGatewayReqSchema = z.object({
  description: z.string().nullish(),
  status: PaymentGatewayStatusEnumZ,
  stripePublishableKey: z.string(),
  stripeSecretKey: z.string().nullish(),
  defaultGateway: z.boolean(),
  type: PaymentGatewayTypeEnumZ,
  // TODO: add better validation for this - one of them should be required
  allowedPaymentMethods: PaymentMethodEnabledSchema.array(),
  paymentConfiguration: PaymentGatewayConfigurationsSchema.nullish(),
});

export type IPaymentGatewayReqSchema = z.infer<typeof PaymentGatewayReqSchema>;

export const PaymentGatewayReqUISchema = PaymentGatewayReqSchema.omit({
  stripePublishableKey: true, // UI uses different field names
  stripeSecretKey: true, // UI uses different field names
  type: true,
  allowedPaymentMethods: true,
}).extend({
  secretKey: z.string().optional(), // only required for new gateways - otherwise can be omitted to keep what is in DB
  publicKey: z.string().nonempty(getRequiredMessage('Public key')),
  creditCardEnabled: z.boolean().optional(),
  directDebitEnabled: z.boolean().optional(),
  achCreditEnabled: z.boolean().optional(),
  usBankTransferEnabled: z.boolean().optional(),
});
export type IPaymentGatewayReqUISchema = z.infer<
  typeof PaymentGatewayReqUISchema
>;

// Form schema without transform for react-hook-form compatibility
export const PaymentGatewayReqUIFormSchema = PaymentGatewayReqSchema.omit({
  stripePublishableKey: true, // UI uses different field names
  stripeSecretKey: true, // UI uses different field names
  type: true,
  allowedPaymentMethods: true,
}).extend({
  secretKey: z.string().optional(), // only required for new gateways - otherwise can be omitted to keep what is in DB
  publicKey: z.string().nonempty(getRequiredMessage('Public key')),
  creditCardEnabled: z.boolean().optional(),
  directDebitEnabled: z.boolean().optional(),
  achCreditEnabled: z.boolean().optional(),
  usBankTransferEnabled: z.boolean().optional(),
  paymentConfiguration: PaymentGatewayConfigurationsFormSchema.nullish(),
});
export type IPaymentGatewayReqUIFormSchema = z.infer<
  typeof PaymentGatewayReqUIFormSchema
>;

// Retained prior name to avoid large changes across codebase
// This is the response schema
export const IPaymentGatewaySchema = z.object({
  id: z.string(),
  description: z.string().nullish(),
  type: PaymentGatewayTypeEnumZ,
  status: PaymentGatewayStatusEnumZ,
  publicKey: z.string(),
  testGateway: z.boolean(),
  defaultGateway: z.boolean(),
  locked: z.boolean(),
  allowedPaymentMethods: PaymentMethodEnabledSchema.array(),
});
export type IPaymentGateway = z.infer<typeof IPaymentGatewaySchema>;

export interface IPaymentGatewayAccount {
  id: string;
  accountId: string;
  paymentGatewayId: string;
  gatewayCustomerId: string;
}

export const PaymentGatewayAccountSchema = BaseResponseSchema.extend({
  accountId: z.string(),
  paymentGatewayId: z.string(),
  gatewayCustomerId: z.string(),
});

export type IPaymentGatewayAccountSchema = z.infer<
  typeof PaymentGatewayAccountSchema
>;

// this response is generated by our own endpoints, versus
// Stripe's built-in SetupIntentResult and SetupIntent types
export interface SetupIntentResponse {
  setupIntentId: string;
}

export interface PaymentGatewayClientSecretResponse {
  clientSecret: string;
}

export const GatewayAccountSchema = z.object({
  id: string(),
  name: string(),
  email: string(),
});

export const PaymentGatewayAccountSummarySchema = BaseResponseSchema.extend({
  paymentGatewayId: string(),
  gatewayCustomerId: string(),
  type: PaymentGatewayTypeEnumZ,
  status: PaymentGatewayStatusEnumZ,
  testGateway: z.boolean(),
  description: string(),
  gatewayAccount: GatewayAccountSchema,
});

export type PaymentGatewayAccountSummary = z.infer<
  typeof PaymentGatewayAccountSummarySchema
>;

export const PaymentGatewayAccountUnlinkSchema = z.object({
  cancellationReason: z
    .string()
    .max(255, 'Reason must not exceed 255 characters')
    .nullish(),
});

export type PaymentGatewayAccountUnlinkReq = z.infer<
  typeof PaymentGatewayAccountUnlinkSchema
>;

export type PaymentGatewayAccountUnlinkUI = PaymentGatewayAccountUnlinkReq;
